import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiChevronRight, FiSearch, FiX, FiInbox, FiHash, FiFilter, FiChevronDown } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { ticketsAPI, notificationsAPI } from '../../services/api';
import ColumnCustomizer, { useColumnPreferences } from '../../components/Common/ColumnCustomizer';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import { runTableFilters } from '../../utils/tableFilters';
import './TicketList.css';

const statusClass = (s) => {
  switch (String(s || '').toUpperCase()) {
    case 'OPEN':
      return 'badge-new';
    case 'IN_PROGRESS':
      return 'badge-meeting';
    case 'ON_HOLD':
      return 'badge-deferred';
    case 'RESOLVED':
      return 'badge-qualified';
    case 'CLOSED':
      return 'badge-closed';
    default:
      return 'badge-new';
  }
};

const priorityClass = (p) => {
  switch (String(p || '').toUpperCase()) {
    case 'HIGH':
      return 'badge-not-interested';
    case 'MEDIUM':
      return 'badge-followup';
    case 'LOW':
      return 'badge-b2c';
    default:
      return 'badge-followup';
  }
};

/** Server uses ACTIVE as a virtual filter (open + in progress). */
const STATUS_API = ['OPEN', 'IN_PROGRESS', 'ON_HOLD', 'RESOLVED', 'CLOSED'];

function matchesIdRef(row, idRef) {
  const raw = String(idRef || '')
    .trim()
    .replace(/^#/, '');
  if (!raw) return true;
  const lower = raw.toLowerCase();
  const ref = String(row.ticket_ref || '').toLowerCase();
  if (ref && ref.includes(lower)) return true;
  if (/^\d+$/.test(raw)) {
    return String(row.ticket_id) === raw;
  }
  return String(row.ticket_id).includes(raw) || ref.includes(lower);
}

function matchesSearch(row, q) {
  const qNorm = String(q || '')
    .trim()
    .toLowerCase();
  if (!qNorm) return true;
  const parts = [
    row.title,
    row.student_first_name,
    row.student_last_name,
    row.student_ref,
    row.agency_display_name,
    row.agency_ref,
    row.assigned_to_name,
    row.created_by_name,
    row.ticket_ref,
    String(row.ticket_id ?? '')
  ];
  const hay = parts.filter(Boolean).join(' ').toLowerCase();
  return hay.includes(qNorm);
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

const TicketList = () => {
  const { t } = useTranslation();
  const ticketColumns = useMemo(
    () => [
      { key: 'ref', label: t('tickets.colRef') },
      { key: 'title', label: t('tickets.colTitle') },
      { key: 'status', label: t('tickets.colStatus') },
      { key: 'priority', label: t('tickets.colPriority') },
      { key: 'tagged', label: t('tickets.colTagged') },
      { key: 'assignee', label: t('tickets.colAssignee') },
      { key: 'created_at', label: t('tickets.colCreatedAt') },
      { key: 'actions', label: t('common.actions', 'Actions'), locked: true }
    ],
    [t]
  );
  const { isVisible, toggleColumn, resetToDefault, columns: ticketColDefs } = useColumnPreferences('tickets', ticketColumns);
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [idRef, setIdRef] = useState('');
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'ACTIVE') {
        params.status = statusFilter;
      }
      const res = await ticketsAPI.list(params);
      let data = res.data || [];
      if (statusFilter === 'ACTIVE') {
        data = data.filter((r) => ['OPEN', 'IN_PROGRESS'].includes(String(r.status || '').toUpperCase()));
      }
      setRawRows(data);
    } catch (err) {
      toast.error(t('tickets.toasts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    load();
    notificationsAPI.markTicketRead()
      .then(() => window.dispatchEvent(new Event('ticket-mark-read')))
      .catch(() => {});
  }, [load]);

  const displayRows = useMemo(() => {
    return rawRows.filter((row) => matchesSearch(row, searchText) && matchesIdRef(row, idRef));
  }, [rawRows, searchText, idRef]);

  const tagLabel = (row) => {
    if (row.student_id) {
      const name = [row.student_first_name, row.student_last_name].filter(Boolean).join(' ') || row.student_ref;
      return `${name} (${row.student_ref || row.student_id})`;
    }
    if (row.agency_id) {
      return `${row.agency_display_name || row.agency_ref || ''} (${row.agency_ref || row.agency_id})`.trim();
    }
    return '—';
  };

  const statusLabel = (code) => {
    const c = String(code || '').toUpperCase();
    const map = {
      OPEN: t('tickets.statusOpen'),
      IN_PROGRESS: t('tickets.statusInProgress'),
      ON_HOLD: t('tickets.statusOnHold'),
      RESOLVED: t('tickets.statusResolved'),
      CLOSED: t('tickets.statusClosed')
    };
    return map[c] || code;
  };

  const columnFilterDefs = useMemo(() => {
    const statusOpts = Array.from(new Set((displayRows || []).map((r) => String(r.status || '')).filter(Boolean)));
    const priorityOpts = Array.from(new Set((displayRows || []).map((r) => String(r.priority || '')).filter(Boolean)));
    const assigneeOpts = Array.from(new Set((displayRows || []).map((r) => String(r.assigned_to_name || '')).filter(Boolean)));
    return [
      { key: 'ref', label: t('tickets.colRef'), field: 'ticket_ref', type: 'text' },
      { key: 'title', label: t('tickets.colTitle'), field: 'title', type: 'text' },
      { key: 'status', label: t('tickets.colStatus'), field: 'status', type: 'multi', options: statusOpts.map((v) => ({ value: v, label: statusLabel(v) })) },
      { key: 'priority', label: t('tickets.colPriority'), field: 'priority', type: 'multi', options: priorityOpts.map((v) => ({ value: v, label: v })) },
      { key: 'tagged', label: t('tickets.colTagged'), field: 'tag_label', type: 'text' },
      { key: 'assignee', label: t('tickets.colAssignee'), field: 'assigned_to_name', type: 'multi', options: assigneeOpts.map((v) => ({ value: v, label: v })) },
      { key: 'created_at', label: t('tickets.colCreatedAt'), field: 'created_at', type: 'date' }
    ];
  }, [displayRows, t]);
  const columnFilterMap = useMemo(() => Object.fromEntries(columnFilterDefs.map((d) => [d.key, d])), [columnFilterDefs]);
  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const normalizedTicketRows = useMemo(
    () => (displayRows || []).map((r) => ({ ...r, tag_label: tagLabel(r) })),
    [displayRows]
  );
  const finalRows = useMemo(
    () => runTableFilters(normalizedTicketRows, tableFilters, tableFilters, [], columnFilterDefs),
    [normalizedTicketRows, tableFilters, columnFilterDefs]
  );

  const resetFilters = () => {
    setStatusFilter('');
    setSearchText('');
    setIdRef('');
  };

  const hasActiveFilters = Boolean(statusFilter || searchText.trim() || idRef.trim());
  const emptyBecauseFilters = !loading && rawRows.length > 0 && displayRows.length === 0;

  return (
    <div className="ticket-list-page page-container">
      <div className="page-header">
        <div>
          <h1>{t('tickets.title')}</h1>
          <p className="text-muted">{t('tickets.subtitle')}</p>
        </div>
        <Link to="/tickets/new" className="btn btn-primary">
          <FiPlus style={{ marginRight: 6 }} />
          {t('tickets.newTicket')}
        </Link>
      </div>

      <div className="ticket-filters-panel">
        <div className="table-toolbar">
          <div className="toolbar-left">
            <div className="filter-field">
              <label htmlFor="ticket-search">{t('tickets.filterSearchLabel')}</label>
              <div className="table-search">
                <FiSearch className="search-icon" aria-hidden />
                <input
                  id="ticket-search"
                  type="search"
                  autoComplete="off"
                  placeholder={t('tickets.listSearchPlaceholder')}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>
            <div className="filter-field">
              <label htmlFor="ticket-id-ref">{t('tickets.filterIdRef')}</label>
              <div className="table-search">
                <FiHash className="search-icon" aria-hidden />
                <input
                  id="ticket-id-ref"
                  type="text"
                  autoComplete="off"
                  placeholder={t('tickets.ticketIdRefPlaceholder')}
                  value={idRef}
                  onChange={(e) => setIdRef(e.target.value)}
                />
              </div>
            </div>
            <div className="filter-field">
              <label htmlFor="ticket-status">{t('tickets.filterStatus')}</label>
              <div className="table-search ticket-search-select">
                <FiFilter className="search-icon" aria-hidden />
                <select
                  id="ticket-status"
                  className="ticket-search-select-input"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">{t('tickets.allStatuses')}</option>
                  <option value="ACTIVE">{t('tickets.statusActive')}</option>
                  {STATUS_API.map((s) => (
                    <option key={s} value={s}>
                      {statusLabel(s)}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="ticket-search-select-chevron" aria-hidden />
              </div>
            </div>
            <div className="filter-field ticket-filter-reset">
              <button
                type="button"
                className="btn btn-outline-secondary"
                style={{ height: 38, whiteSpace: 'nowrap' }}
                disabled={!hasActiveFilters}
                onClick={resetFilters}
              >
                <FiX size={16} style={{ marginRight: 6 }} />
                {t('tickets.resetFilters')}
              </button>
            </div>
            <ColumnCustomizer
              columns={ticketColDefs}
              isVisible={isVisible}
              toggleColumn={toggleColumn}
              resetToDefault={resetToDefault}
            />
          </div>
        </div>
        {!loading && rawRows.length > 0 && (
          <div className="ticket-filter-meta">
            {t('tickets.showingCount', { shown: displayRows.length, total: rawRows.length })}
          </div>
        )}
      </div>

      <div className="card ticket-table-card">
        {loading ? (
          <div className="loading-spinner" style={{ padding: '2rem' }}>
            <div className="spinner" />
          </div>
        ) : !rawRows.length ? (
          <div className="empty-state-tickets">
            <FiInbox size={48} />
            <p style={{ margin: 0, fontSize: 15 }}>{t('tickets.empty')}</p>
          </div>
        ) : emptyBecauseFilters ? (
          <div className="empty-state-tickets">
            <FiSearch size={40} />
            <p style={{ margin: 0, fontSize: 15 }}>{t('tickets.noMatchFilters')}</p>
            <button type="button" className="btn btn-sm btn-outline-primary" style={{ marginTop: 12 }} onClick={resetFilters}>
              {t('tickets.resetFilters')}
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  {isVisible('ref') && <UnifiedFilterHeaderCell column={columnFilterMap.ref} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('title') && <UnifiedFilterHeaderCell column={columnFilterMap.title} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('status') && <UnifiedFilterHeaderCell column={columnFilterMap.status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('priority') && <UnifiedFilterHeaderCell column={columnFilterMap.priority} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('tagged') && <UnifiedFilterHeaderCell column={columnFilterMap.tagged} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('assignee') && <UnifiedFilterHeaderCell column={columnFilterMap.assignee} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('created_at') && <UnifiedFilterHeaderCell column={columnFilterMap.created_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  <th>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {finalRows.map((row) => (
                  <tr key={row.ticket_id}>
                    {isVisible('ref') && (
                      <td>
                        <div className="ticket-ref-cell">
                          <strong>
                            <Link to={`/tickets/${row.ticket_id}`} className="ticket-ref-link">
                              {row.ticket_ref || `#${row.ticket_id}`}
                            </Link>
                          </strong>
                          {row.ticket_ref ? (
                            <span className="ticket-id-muted">ID {row.ticket_id}</span>
                          ) : null}
                        </div>
                      </td>
                    )}
                    {isVisible('title') && <td className="ticket-title-cell">{row.title}</td>}
                    {isVisible('status') && (
                      <td>
                        <span className={`badge ${statusClass(row.status)}`}>{statusLabel(row.status)}</span>
                      </td>
                    )}
                    {isVisible('priority') && (
                      <td>
                        <span className={`badge ${priorityClass(row.priority)}`}>{row.priority}</span>
                      </td>
                    )}
                    {isVisible('tagged') && <td className="ticket-tagged-cell">{tagLabel(row)}</td>}
                    {isVisible('assignee') && <td>{row.assigned_to_name || '—'}</td>}
                    {isVisible('created_at') && <td>{formatTime(row.created_at) || '—'}</td>}
                    <td>
                      <Link to={`/tickets/${row.ticket_id}`} className="btn btn-sm btn-outline-primary">
                        {t('tickets.open')}
                        <FiChevronRight size={16} style={{ marginLeft: 4 }} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketList;
